import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Grid, LinearProgress, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { format, startOfMonth } from 'date-fns';
import { apiErrorMessage, hrService, HRSummaryReport } from '../../services/hrService';
import { formatCurrency } from '../../utils/format';
import { formatDateBR } from '../../utils/date';
import { REIMBURSEMENT_STATUS_LABELS, ReimbursementStatus } from '../../types/collaborator';

const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const SimpleTable: React.FC<{ headers: string[]; rows: (string | number)[][]; empty: string }> = ({ headers, rows, empty }) => (
    <TableContainer component={Paper} variant="outlined">
        <Table size="small">
            <TableHead>
                <TableRow>{headers.map((h, i) => <TableCell key={h} align={i === headers.length - 1 ? 'right' : 'left'}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, i) => (
                    <TableRow key={i}>{row.map((cell, j) => <TableCell key={j} align={j === row.length - 1 ? 'right' : 'left'}>{cell}</TableCell>)}</TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={headers.length} align="center">{empty}</TableCell></TableRow>}
            </TableBody>
        </Table>
    </TableContainer>
);

const RelatoriosList: React.FC = () => {
    const [start, setStart] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [end, setEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [report, setReport] = useState<HRSummaryReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!start || !end) return;
        try {
            setLoading(true);
            setReport(await hrService.summary(start, end));
            setError(null);
        } catch (err) {
            setError(apiErrorMessage(err, 'Erro ao gerar o relatório.'));
        } finally {
            setLoading(false);
        }
    }, [start, end]);

    useEffect(() => { load(); }, [load]);

    const exportHours = () => {
        if (!report) return;
        const lines = [
            ['Data', 'Colaborador', 'Projeto', 'Horas', 'Descrição'].map(csvCell).join(';'),
            ...report.hours.entries.map((e) => [formatDateBR(e.date), e.user_name, e.project_name, String(e.hours).replace('.', ','), e.description].map(csvCell).join(';')),
        ];
        // BOM para o Excel abrir os acentos corretamente
        const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `horas_${report.period.start}_${report.period.end}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const headcountTotal = report ? Object.values(report.headcount).reduce((a, b) => a + b, 0) : 0;
    const reimbTotal = report ? Object.values(report.reimbursements).reduce((a, b) => a + b.amount, 0) : 0;
    const vacationDays = report ? report.vacations.reduce((a, v) => a + v.business_days_in_period, 0) : 0;

    return (
        <Box>
            <Typography variant="h5" gutterBottom>Relatórios</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center" mb={2}>
                <TextField type="date" size="small" label="De" InputLabelProps={{ shrink: true }} value={start}
                    onChange={(e) => setStart(e.target.value)} sx={{ flex: '1 1 150px' }} />
                <TextField type="date" size="small" label="Até" InputLabelProps={{ shrink: true }} value={end}
                    onChange={(e) => setEnd(e.target.value)} sx={{ flex: '1 1 150px' }}
                    error={!!start && !!end && end < start} helperText={end < start ? 'Antes da data inicial' : ''} />
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportHours}
                    disabled={!report || report.hours.entries.length === 0}>
                    Exportar horas (CSV)
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {report && (
                <>
                    <Grid container spacing={2} mb={3}>
                        {[
                            { label: 'Horas lançadas', value: `${report.hours.total}h` },
                            { label: 'Pessoas ativas', value: headcountTotal },
                            { label: 'Reembolsos no período', value: formatCurrency(reimbTotal) },
                            { label: 'Dias úteis de férias', value: vacationDays },
                        ].map((kpi) => (
                            <Grid item xs={6} md={3} key={kpi.label}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="body2" color="text.secondary">{kpi.label}</Typography>
                                        <Typography variant="h5" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>{kpi.value}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>Horas por projeto</Typography>
                            <SimpleTable headers={['Projeto', 'Horas']} empty="Nenhuma hora lançada no período."
                                rows={report.hours.by_project.map((r) => [r.project_name, r.hours])} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>Horas por pessoa</Typography>
                            <SimpleTable headers={['Colaborador', 'Horas']} empty="Nenhuma hora lançada no período."
                                rows={report.hours.by_person.map((r) => [r.user_name, r.hours])} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>Reembolsos por situação</Typography>
                            <SimpleTable headers={['Situação', 'Quantidade', 'Valor']} empty="Nenhum reembolso no período."
                                rows={Object.entries(report.reimbursements).map(([status, v]) =>
                                    [REIMBURSEMENT_STATUS_LABELS[status as ReimbursementStatus] || status, v.count, formatCurrency(v.amount)])} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>Quadro de pessoal (ativos)</Typography>
                            <SimpleTable headers={['Perfil', 'Pessoas']} empty="Nenhum usuário ativo."
                                rows={Object.entries(report.headcount).map(([role, n]) => [role, n])} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>Férias aprovadas no período</Typography>
                            <SimpleTable headers={['Colaborador', 'Início', 'Fim', 'Dias úteis no período']} empty="Ninguém de férias no período."
                                rows={report.vacations.map((v) => [v.user_name, formatDateBR(v.start_date), formatDateBR(v.end_date), v.business_days_in_period])} />
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default RelatoriosList;
